"use client";
import LanguageDropdown from "@/components/dashboard/UserDropdown/LanguageDropdown";
import { useSettings } from "@/context/settingsContext";
import { useT } from "@/context/TranslationContext";
import { useUser } from "@/context/UserContext";
import ApiPath from "@/network/api/apiPath";
import NetworkService from "@/network/service/networkService";
import { getSettingValue, normalizeHref } from "@/utils/utils";
import Cookies from "js-cookie";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import GridMegaMenu from "./GridMegaMenu";
import GridMegaMenuMobile from "./GridMegaMenuMobile";
import ListMegaMenuMobile from "./ListMegaMenuMobile";
import ListWithPreviewMegaMenu from "./ListWithPreviewMegaMenu";

const Header = () => {
  const network = new NetworkService();
  const [navigation, setNavigation] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const homePaths = [
    "/",
    "/virtual-cards",
    "/bill-payment",
    "/gift-cards",
    "/p2p-trading",
  ];
  const isHome = homePaths.includes(pathname);
  const [openMenu, setOpenMenu] = useState(null);
  const { settings } = useSettings();
  const siteLogo = getSettingValue(settings, "site_logo");
  const siteLogoHeight = getSettingValue(settings, "site_logo_height");
  const { user, loading } = useUser();
  const t = useT();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggle = (menuKey) => {
    setOpenMenu((prev) => (prev === menuKey ? null : menuKey));
  };

  const fetchNavigation = async () => {
    setNavigationLoading(true);
    try {
      // Get current locale from cookie
      const locale = Cookies.get("NEXT_LOCALE") || "en";

      // Add locale as query parameter
      const res = await network.globalGet(`${ApiPath.navigation}?locale=${locale}`);
      if (res?.status === "completed") {
        setNavigation(res?.data?.data || {});
      } else {
        setNavigation({});
      }
    } catch (error) {
      console.error("Failed to fetch navigation:", error);
      setNavigation({});
    } finally {
      setNavigationLoading(false);
    }
  };

  const isAuthenticated = () => {
    const token = Cookies.get("token");
    const kycVerify = user?.boarding_steps?.completed;

    return (
      token !== undefined &&
      token !== null &&
      token.length > 0 &&
      kycVerify === true
    );
  };

  useEffect(() => {
    fetchNavigation();
  }, []);

  // Refetch navigation when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchNavigation();
    };

    window.addEventListener("languageChange", handleLanguageChange);
    return () => window.removeEventListener("languageChange", handleLanguageChange);
  }, []);

  const headerReady = !navigationLoading && !loading;

  if (!headerReady) {
    return (
      <div
        className={`${
          !isHome ? "bg-white top-[0px]" : "bg-transparent top-[30px]"
        } fixed w-full z-50`}
      >
        <div
          className={`${
            !isHome ? "my-[20px] sm:my-[30px]" : "my-0"
          } custom-container mx-auto relative`}
        >
          <div className="bg-[#F2F2F2] ps-[16px] pe-[10px] h-[70px] rounded-[16px] animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <header
        className={`${
          !isHome ? "bg-white top-[0px]" : "bg-transparent top-[30px]"
        } fixed w-full z-50`}
      >
        <div
          className={`${
            !isHome ? "my-[20px] sm:my-[30px]" : "my-0"
          } custom-container mx-auto relative`}
        >
          <div className="bg-[#F2F2F2] ps-[16px] pe-[10px] py-[15px] lg:py-0 rounded-[16px]">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Link
                  href="/"
                  className={`logo h-[25px] sm:h-[${siteLogoHeight}px] w-auto`}
                >
                  {siteLogo ? (
                    <Image
                      src={siteLogo}
                      alt="Logo"
                      width={100}
                      height={100}
                      unoptimized
                      className="h-full w-auto"
                    />
                  ) : (
                    <div className="h-[30px] w-[100px] bg-gray-100 animate-pulse rounded"></div>
                  )}
                </Link>
              </div>
              <nav className="hidden lg:flex items-center space-x-7.5">
                {navigation?.header?.menus?.map((link) => {
                  const normalizedSlug = normalizeHref(link.url);
                  const isActive = pathname === normalizedSlug;

                  // if no mega menu
                  if (!link.has_megamenu) {
                    return (
                      <Link
                        key={link.name}
                        href={normalizedSlug}
                        className={`text-[14px] font-semibold py-[25px] ${
                          isActive
                            ? "text-user-primary"
                            : "text-user-text hover:text-user-primary"
                        }`}
                      >
                        {link.name}
                      </Link>
                    );
                  }
                  // if mega menu exist
                  else {
                    if (link.megamenu_type === 1) {
                      return (
                        <ListWithPreviewMegaMenu key={link.name} menu={link} />
                      );
                    } else if (link.megamenu_type === 2) {
                      return <GridMegaMenu key={link.name} menu={link} />;
                    }
                    return null;
                  }

                  return null;
                })}
              </nav>
              <div className="flex items-center space-x-1 lg:space-x-2">
                <LanguageDropdown />
                {!isAuthenticated() ? (
                  <>
                    <Link
                      href="/auth/login"
                      className="px-[24px] h-[40px] hidden lg:flex justify-center items-center text-[14px] font-semibold text-user-text hover:text-user-primary"
                    >
                      {t("authPages.login")}
                    </Link>
                    <Link
                      href="/auth/register"
                      className="group primary-button primary-button-md"
                    >
                      <span className="primary-button-hover-effect"></span>
                      <span className="primary-button-text">
                        {t("authPages.signUp")}
                      </span>
                    </Link>
                  </>
                ) : (
                  <Link
                    href="/dashboard"
                    className="group primary-button primary-button-md"
                  >
                    <span className="primary-button-hover-effect"></span>
                    <span className="primary-button-text">Dashboard</span>
                  </Link>
                )}

                <button
                  onClick={toggleMenu}
                  className="flex lg:hidden flex-col justify-center items-center w-8 h-8 space-y-1.5 focus:outline-none"
                  aria-label="Toggle menu"
                >
                  <span
                    className={`block w-5 h-0.5 bg-user-paragraph transition-all duration-300 ${
                      isOpen ? "rotate-45 translate-y-2" : ""
                    }`}
                  ></span>
                  <span
                    className={`block w-5 h-0.5 bg-user-paragraph transition-all duration-300 ${
                      isOpen ? "opacity-0" : ""
                    }`}
                  ></span>
                  <span
                    className={`block w-5 h-0.5 bg-user-paragraph transition-all duration-300 ${
                      isOpen ? "-rotate-45 -translate-y-2" : ""
                    }`}
                  ></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-100 lg:hidden transition-opacity duration-300 ${
          isOpen
            ? "opacity-50 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMenu}
      ></div>

      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-xl z-100 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6">
          <div className="flex items-center space-x-1 mb-8">
            <Link
              href="/"
              onClick={toggleMenu}
              className="logo h-[30px] w-auto"
            >
              {siteLogo ? (
                <Image
                  src={siteLogo}
                  alt="Logo"
                  width={100}
                  height={100}
                  unoptimized
                  className="h-full w-auto"
                />
              ) : (
                <div className="h-[30px] w-[100px] bg-gray-100 animate-pulse rounded"></div>
              )}
            </Link>
          </div>
          <nav className="space-y-2.5 h-[calc(100vh-100px)] overflow-y-auto overflow-x-hidden">
            {navigation?.header?.menus?.map((link) => {
              const normalizedSlug = normalizeHref(link.url);
              const isActive = pathname === normalizedSlug;
              const menuKey = `${link.megamenu_type}-${link.id}`;

              if (!link.has_megamenu) {
                return (
                  <Link
                    key={link.name}
                    href={normalizedSlug}
                    onClick={toggleMenu}
                    className={`block text-[14px] font-semibold py-2 transition-colors duration-200 ${
                      isActive
                        ? "text-user-primary"
                        : "text-user-text hover:text-user-primary"
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              } else {
                if (link.megamenu_type === 1) {
                  return (
                    <ListMegaMenuMobile
                      key={`list-${link.id}`}
                      open={openMenu === menuKey}
                      toggle={() => toggle(menuKey)}
                      menu={link}
                    />
                  );
                } else if (link.megamenu_type === 2) {
                  return (
                    <GridMegaMenuMobile
                      key={`grid-${link.id}`}
                      open={openMenu === menuKey}
                      toggle={() => toggle(menuKey)}
                      menu={link}
                    />
                  );
                }
                return null;
              }
            })}
            <div className="lg:hidden flex flex-col justify-center space-y-2.5">
              {!isAuthenticated() ? (
                <>
                  <Link
                    href="/auth/login"
                    className="px-[24px] h-[40px] hidden lg:flex justify-center items-center text-[14px] font-semibold text-user-text hover:text-user-primary"
                  >
                    {t("authPages.login")}
                  </Link>
                  <Link
                    href="/auth/register"
                    className="group primary-button primary-button-md"
                  >
                    <span className="primary-button-hover-effect"></span>
                    <span className="primary-button-text">
                      {t("authPages.signUp")}
                    </span>
                  </Link>
                </>
              ) : (
                <Link
                  href="/dashboard"
                  className="group primary-button primary-button-md"
                >
                  <span className="primary-button-hover-effect"></span>
                  <span className="primary-button-text">Dashboard</span>
                </Link>
              )}
            </div>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Header;
